import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { SharedModule } from 'src/app/modules/shared/shared.module';
import { LoadingComponent } from '../../auth/components/loading/loading.component';
import { CommonModule } from '@angular/common';
import { OperationService } from '../abstraction/operation.service';
import { AuthService } from '../../auth/abstraction/auth.service';
import { ActivatedRoute } from '@angular/router';
import { IOperationStatus, OPERATION_STATUS } from '../models/operations';
import { ViewService } from 'src/app/modules/shared/services/view.service';
import { Utils } from '../../auth/utils/Utils';
import { AuthSocketService } from '../../auth/services/auth-socket.service';
import { environment } from 'src/environments/environment';
import { MatDialogRef } from '@angular/material/dialog';
import { AlertDialogComponent } from 'src/app/modules/shared/components/dialogs/alert-dialog/alert-dialog.component';

@Component({
  selector: 'app-operations',
  standalone: true,
  imports: [SharedModule, LoadingComponent, CommonModule],
  templateUrl: './operations.component.html',
  styleUrl: './operations.component.scss',
})
export class OperationsComponent implements OnInit, OnDestroy {
  operationService = inject(OperationService);
  auth = inject(AuthService);
  route = inject(ActivatedRoute);
  viewSvc = inject(ViewService);
  authSocketService = inject(AuthSocketService);
  loading = true;
  dataStatus: IOperationStatus;
  errorMessage: string;
  OPERATION_STATUS = OPERATION_STATUS;
  dataDeposit;
  isDesktop: boolean;
  signature: string;
  attemptedStates: { [key: string]: boolean } = {
    dataComplete: false,
    priceLocked: false,
    approved: false,
  };
  redirect = false;
  ngOnInit(): void {
    this.isDesktop = Utils.onDesktop();
    this.route.queryParams.subscribe((params) => {
      if (params['signature']) {
        this.operationService.getOperationStatus(params['signature']).subscribe({
          next: (resp) => {
            this.dataStatus = resp;
            this.signature = params['signature'];
            this.executeBasedOnStatus(resp, params['signature']);
            this.auth.redirectPath = signal('operation');
            this.auth.saveRedirectPath();
          },
          error: () => {
            this.loading = false;
            this.errorMessage =
              '<b>Ha ocurrido un error</b> <span>No ha sido posible iniciar la operación.</span>';
          },
        });
        this.authSocketService.connectWidgetSocket(params['signature']);
        this.listenToSocket();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.authSocketService.widgetSocket) {
      this.authSocketService.disconnectWidget();
    }
  }

  executeBasedOnStatus(data: IOperationStatus, signature: string) {
    let depositAmount;
    switch (data.status) {
      case OPERATION_STATUS.DATA_COMPLETE:
        this.makeLockPrice(signature);
        break;
      case OPERATION_STATUS.PRICE_LOCKED:
        if (!this.attemptedStates['priceLocked'])
          this.operationService.checkCompliance(signature).subscribe({
            next: (status) => {
              this.dataStatus = status;
              this.attemptedStates['priceLocked'] = true;
              this.executeBasedOnStatus(status, signature);
            },
            error: (err) => {
              if (err.error.internalStatus === 'EXPIRED_LOCK') {
                this.makeLockPrice(signature);
              } else {
                this.showAlert(
                  'Ha ocurrido un error',
                  'Falló al chequear la información.',
                  err.status + (err.error?.internalStatus ? '-' + err.error?.internalStatus : ''),
                );
                this.loading = false;
                this.errorMessage =
                  '<b>Ha ocurrido un error</b> <span>Falló al chequear la información.</span>';
              }
            },
          });
        else {
          this.showAlert('Ha ocurrido un error', 'Falló al actualizar el estado.');
          this.loading = false;
          this.errorMessage =
            '<b>Ha ocurrido un error</b> <span>Falló al actualizar el estado.</span>';
        }
        break;
      case OPERATION_STATUS.APPROVED:
        if (!this.attemptedStates['approved'])
          this.operationService.sendRules(signature).subscribe({
            next: (status) => {
              this.dataStatus = status;
              this.attemptedStates['approved'] = true;
              this.executeBasedOnStatus(status, signature);
            },
            error: (err) => {
              this.showAlert(
                'Ha ocurrido un error',
                'Ocurrió un error al realizar el envío.',
                err.status + (err.error?.internalStatus ? '-' + err.error?.internalStatus : ''),
              );
              this.loading = false;
              this.errorMessage =
                '<b>Ha ocurrido un error</b> <span>Falló al realizar el envío.</span>';
            },
          });
        else {
          this.showAlert('Ha ocurrido un error', 'Falló al actualizar el estado.', 'UPDATE_STATUS');
          this.loading = false;
          this.errorMessage =
            '<b>Ha ocurrido un error</b> <span>Falló al actualizar el estado.</span>';
        }
        break;

      case OPERATION_STATUS.WAITING:
        depositAmount =
          this.dataStatus.data.side === 'BUY' && this.dataStatus.data.withdrawCost
            ? Number(this.dataStatus.data.depositAmount) +
              Number(this.dataStatus.data.withdrawCost) * Number(this.dataStatus.data.price)
            : this.dataStatus.data.depositAmount;
        this.dataDeposit = {
          depositAddresses: this.dataStatus.data?.depositAddresses,
          depositAmount: depositAmount,
          withdrawCost: this.dataStatus.data?.withdrawCost,
          side: this.dataStatus.data.side,
          asset: this.dataStatus.data.asset,
          price: this.dataStatus.data.price,
          withdrawAmount:
            this.dataStatus.data.side === 'BUY'
              ? Number(this.dataStatus.data.depositAmount) / Number(this.dataStatus.data.price)
              : Number(this.dataStatus.data.depositAmount) * Number(this.dataStatus.data.price),
          against: this.dataStatus.data.against,
          availableDepositNetworks: this.dataStatus.data?.availableDepositNetworks,
          depositAlias: this.dataStatus.data?.depositAlias,
        };
        break;
      case OPERATION_STATUS.COMPLETED:
        window.location.href = `${environment.apiManteca}/widget/next?signature=${signature}`;
        break;
      case OPERATION_STATUS.FAILED:
        window.location.href = `${environment.apiManteca}/widget/next?signature=${signature}`;
        break;
    }
  }

  listenToSocket() {
    let socket = this.authSocketService.widgetSocket;
    socket.on('widgetStatusUpdate', () => {
      this.operationService.getOperationStatus(this.signature).subscribe((data) => {
        if (
          (data.status === OPERATION_STATUS.COMPLETED || data.status === OPERATION_STATUS.FAILED) &&
          !this.redirect
        ) {
          this.executeBasedOnStatus(data, this.signature);
          this.redirect = true;
        }
      });
    });
  }

  makeLockPrice(signature: string) {
    if (!this.attemptedStates['dataComplete'])
      this.operationService.lockPriceWidget(signature).subscribe({
        next: (status) => {
          this.dataStatus = status;
          this.attemptedStates['dataComplete'] = true;
          this.executeBasedOnStatus(status, signature);
        },
        error: (err) => {
          this.showAlert(
            'Ha ocurrido un error',
            'Ocurrió un error al realizar el lock.',
            err.status + (err.error?.internalStatus ? '-' + err.error?.internalStatus : ''),
          );
          this.loading = false;
          this.errorMessage = '<b>Ha ocurrido un error</b> <span>Falló al realizar el lock.</span>';
        },
      });
    else {
      this.showAlert('Ha ocurrido un error', 'Falló al actualizar el estado.', 'UPDATE_STATUS');
      this.loading = false;
      this.errorMessage = '<b>Ha ocurrido un error</b> <span>Falló al actualizar el estado.</span>';
    }
  }

  private showAlert(
    title: string,
    content: string,
    errorCode?: string,
  ): MatDialogRef<AlertDialogComponent> {
    return this.viewSvc.showAlert({
      title,
      content,
      errorCode,
      isError: true,
    });
  }
}
